import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, EmptyBox } from "./shared";
import { Download, ExternalLink, Loader2, Package, CheckCircle2, AlertTriangle, Search, Rocket, X, XCircle, FileText } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const seriesOf = (t) => {
  const tags = Array.isArray(t.tags) ? t.tags : [];
  const s = tags.find((x) => /series$/i.test(x));
  return s || t.category || "Uncategorized";
};
const statusOf = (t) => {
  if (t.lifecycle_status === "archived") return { label: "Archived", cls: "text-gray-400 border-white/15 bg-white/5" };
  return t.is_active
    ? { label: "Live", cls: "text-emerald-300 border-emerald-400/30 bg-emerald-500/10" }
    : { label: "Hidden", cls: "text-amber-300 border-amber-400/30 bg-amber-500/10" };
};

export function ProductDownloads() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [pubCfg, setPubCfg] = useState(null);
  const [pub, setPub] = useState(null); // { template, loading, preflight, publishing, result }

  useEffect(() => {
    api.get("/admin/templates").then((r) => setItems(r.data || [])).catch(() => setItems([]));
    api.get("/admin/publish/config").then((r) => setPubCfg(r.data)).catch(() => setPubCfg({ configured: false }));
  }, []);

  const openPublish = async (t) => {
    setPub({ template: t, loading: true });
    try {
      const r = await api.get(`/admin/publish/${t.id}/preflight`);
      setPub({ template: t, loading: false, preflight: r.data });
    } catch (e) {
      toast.error(formatApiError(e));
      setPub(null);
    }
  };

  const confirmPublish = async () => {
    setPub((p) => ({ ...p, publishing: true }));
    try {
      const r = await api.post(`/admin/publish/${pub.template.id}`, { confirm: true });
      setPub((p) => ({ ...p, publishing: false, result: r.data }));
      toast.success("Published to Production (as Draft)");
    } catch (e) {
      setPub((p) => ({ ...p, publishing: false }));
      toast.error(formatApiError(e));
    }
  };


  const ready = useMemo(() => (items || []).filter((t) => t.source_key), [items]);
  const pending = useMemo(() => (items || []).filter((t) => !t.source_key && t.lifecycle_status !== "archived"), [items]);
  const filtered = useMemo(() => {
    if (!q) return ready;
    const s = q.toLowerCase();
    return ready.filter((t) => `${t.title} ${seriesOf(t)} ${t.category}`.toLowerCase().includes(s));
  }, [ready, q]);

  const download = async (t) => {
    setBusyId(t.id);
    try {
      const r = await api.get(`/templates/${t.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${t.slug || "template"}-v${t.current_version || "latest"}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloading ${t.title}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const preview = (t) => {
    const url = t.live_demo ? `${BACKEND}/api/demo/${t.id}/` : `${BACKEND}/template/${t.slug || t.id}`;
    window.open(url, "_blank", "noopener");
  };

  const downloadSheet = async (t) => {
    try {
      const r = await api.get(`/admin/templates/${t.id}/registration-sheet`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${t.slug || "template"}-registration-sheet.md`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Registration sheet downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (items === null) return <Loader />;

  return (
    <div data-testid="product-downloads">
      <Head title="Product Downloads" desc="Every finished template with a ready-to-upload ZIP. Download, then upload on your live site via Admin → Templates to publish." />

      {/* how-to strip */}
      <div className="bg-navy-800 border border-white/10 p-5 mb-8 flex items-start gap-3" data-testid="downloads-howto">
        <Package className="text-gold flex-none mt-0.5" size={20} />
        <div className="text-sm text-gray-300">
          <strong className="text-white">One place for every build.</strong> Click <em>Download</em> to save the ZIP, then on your live site go to
          <span className="text-gold"> Admin → Templates → Files, Versions &amp; Live Demo</span> and upload it. This list updates automatically whenever a product is built or re-versioned.
        </div>
      </div>

      {/* search */}
      <div className="relative max-w-sm mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" data-testid="downloads-search"
          className="w-full bg-navy-800 border border-white/15 text-white pl-10 pr-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
      </div>

      {filtered.length === 0 ? (
        <EmptyBox text={q ? "No products match your search." : "No packaged products yet. Build one in the Product Factory and it'll appear here."} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="downloads-grid">
          {filtered.map((t) => {
            const st = statusOf(t);
            return (
              <div key={t.id} className="bg-navy-800 border border-white/10 flex flex-col overflow-hidden" data-testid={`download-card-${t.slug || t.id}`}>
                <div className="h-36 bg-navy-700 relative overflow-hidden">
                  {t.preview_image
                    ? <img src={t.preview_image} alt={t.title} className="w-full h-full object-cover" />
                    : <div className="w-full h-full grid place-items-center text-gray-600 text-xs uppercase tracking-wide">{t.category}</div>}
                  <span className={`absolute top-3 left-3 text-[10px] uppercase tracking-wider px-2 py-0.5 border ${st.cls}`}>{st.label}</span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="text-[11px] text-gold uppercase tracking-wider mb-1">{seriesOf(t)}</div>
                  <h3 className="font-heading text-white text-lg leading-snug mb-2">{t.title}</h3>
                  <div className="flex items-center gap-3 text-xs text-gray-400 mb-4">
                    <span className="inline-flex items-center gap-1"><CheckCircle2 size={13} className="text-emerald-400" /> v{t.current_version || "1.0.0"}</span>
                    <span>${t.template_price}</span>
                    {t.source_size ? <span>{(t.source_size / 1048576).toFixed(1)} MB</span> : null}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-auto">
                    <button onClick={() => preview(t)} data-testid={`download-preview-${t.slug || t.id}`}
                      className="flex items-center justify-center gap-1.5 border border-white/20 text-white py-2.5 text-sm hover:border-gold/50 hover:text-gold transition-colors">
                      <ExternalLink size={14} /> Preview
                    </button>
                    <button onClick={() => download(t)} disabled={busyId === t.id} data-testid={`download-zip-${t.slug || t.id}`}
                      className="flex items-center justify-center gap-1.5 bg-gold text-navy py-2.5 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
                      {busyId === t.id ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />} ZIP
                    </button>
                  </div>
                  <button onClick={() => downloadSheet(t)} data-testid={`registration-sheet-${t.slug || t.id}`}
                    className="mt-2 flex items-center justify-center gap-1.5 border border-white/15 text-gray-200 py-2.5 text-sm font-semibold hover:border-white/40 hover:text-white transition-colors">
                    <FileText size={14} /> Registration Sheet
                  </button>
                  {pubCfg?.configured && (
                    <button onClick={() => openPublish(t)} data-testid={`publish-to-prod-${t.slug || t.id}`}
                      className="mt-2 flex items-center justify-center gap-1.5 border border-emerald-400/40 text-emerald-300 py-2.5 text-sm font-semibold hover:bg-emerald-500/10 transition-colors">
                      <Rocket size={14} /> Publish to Production
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* products still needing a file */}
      {pending.length > 0 && (
        <div className="mt-12" data-testid="downloads-pending">
          <div className="flex items-center gap-2 text-amber-300 text-sm mb-3"><AlertTriangle size={16} /> Awaiting a packaged file ({pending.length})</div>
          <div className="flex flex-wrap gap-2">
            {pending.map((t) => (
              <span key={t.id} className="text-xs text-gray-400 border border-white/10 bg-white/5 px-3 py-1.5">{t.title}</span>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">These products have a catalog entry but no uploaded ZIP yet — build & upload one to make them downloadable.</p>
        </div>
      )}

      {pub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="publish-modal">
          <div className="bg-navy-800 border border-white/15 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div className="flex items-center gap-2 text-white font-heading text-lg">
                <Rocket size={18} className="text-emerald-300" /> Publish to Production
              </div>
              <button onClick={() => setPub(null)} data-testid="publish-close" className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>

            <div className="p-6">
              {pub.loading && <div className="flex items-center gap-2 text-gray-300 text-sm"><Loader2 className="animate-spin" size={16} /> Running pre-publish checks…</div>}

              {/* SUCCESS REPORT */}
              {pub.result && (
                <div data-testid="publish-success">
                  <div className="flex items-center gap-2 text-emerald-300 font-semibold mb-4"><CheckCircle2 size={18} /> Publish completed successfully</div>
                  <ul className="space-y-2 mb-5">
                    {pub.result.steps.map((s, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-200"><CheckCircle2 size={14} className="text-emerald-400" /> {s.label}</li>
                    ))}
                  </ul>
                  <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-400/30 p-3 mb-5">{pub.result.message}</div>
                  <div className="space-y-2">
                    {pub.result.links?.production_live_demo && (
                      <a href={pub.result.links.production_live_demo} target="_blank" rel="noopener" data-testid="link-prod-demo"
                        className="flex items-center gap-2 text-sm text-gold hover:underline"><ExternalLink size={14} /> Production Live Demo</a>
                    )}
                    {pub.result.links?.production_template && (
                      <a href={pub.result.links.production_template} target="_blank" rel="noopener" data-testid="link-prod-template"
                        className="flex items-center gap-2 text-sm text-gold hover:underline"><ExternalLink size={14} /> Production Template</a>
                    )}
                    {pub.result.links?.version_history && (
                      <a href={pub.result.links.version_history} target="_blank" rel="noopener" data-testid="link-prod-versions"
                        className="flex items-center gap-2 text-sm text-gold hover:underline"><ExternalLink size={14} /> Version History</a>
                    )}
                  </div>
                  <button onClick={() => setPub(null)} data-testid="publish-done" className="mt-6 w-full bg-gold text-navy py-2.5 text-sm font-semibold hover:bg-gold-light">Done</button>
                </div>
              )}

              {/* CHECKLIST + SUMMARY */}
              {pub.preflight && !pub.result && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">Pre-publish checklist</div>
                  <ul className="space-y-2 mb-5" data-testid="publish-checklist">
                    {pub.preflight.checks.map((c) => (
                      <li key={c.key} className="flex items-start gap-2 text-sm">
                        {c.ok ? <CheckCircle2 size={15} className="text-emerald-400 mt-0.5 flex-none" /> : <XCircle size={15} className="text-red-400 mt-0.5 flex-none" />}
                        <span className={c.ok ? "text-gray-200" : "text-red-300"}>
                          {c.label}{!c.ok && c.detail ? <span className="block text-xs text-red-400/80">{c.detail}</span> : null}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="bg-navy-700 border border-white/10 p-4 mb-5 text-sm" data-testid="publish-summary">
                    <div className="text-gray-400 text-xs uppercase tracking-wider mb-2">Publishing</div>
                    {[
                      ["Product", pub.preflight.summary.product],
                      ["Version", pub.preflight.summary.version],
                      ["Destination", pub.preflight.summary.destination],
                      ["Category", pub.preflight.summary.category],
                      ["Price", pub.preflight.summary.price != null ? `$${pub.preflight.summary.price}` : "—"],
                      ["Preview Image", pub.preflight.summary.preview_image ? "✓" : "✗"],
                      ["Live Demo", pub.preflight.summary.live_demo ? "✓" : "✗"],
                      ["ZIP", pub.preflight.summary.zip ? "✓" : "✗"],
                      ["QA", pub.preflight.summary.qa],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between py-0.5 text-gray-200"><span className="text-gray-400">{k}</span><span>{v}</span></div>
                    ))}
                  </div>

                  {!pub.preflight.ready && (
                    <div className="text-xs text-red-300 bg-red-500/10 border border-red-400/30 p-3 mb-4 flex items-start gap-2">
                      <AlertTriangle size={14} className="mt-0.5 flex-none" /> This product is incomplete and cannot be published. Fix the items above first.
                    </div>
                  )}
                  {pub.preflight.ready && (
                    <div className="text-sm text-gray-200 mb-4">Publish <strong>{pub.preflight.summary.product}</strong> to Production? It will arrive as a <strong>Draft</strong> — customers won't see it until you publish it on the live site.</div>
                  )}

                  <div className="flex gap-2">
                    <button onClick={() => setPub(null)} className="flex-1 border border-white/20 text-white py-2.5 text-sm hover:border-white/40">Cancel</button>
                    <button onClick={confirmPublish} disabled={!pub.preflight.ready || pub.publishing} data-testid="publish-confirm"
                      className="flex-1 flex items-center justify-center gap-2 bg-emerald-500 text-navy py-2.5 text-sm font-semibold hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed">
                      {pub.publishing ? <Loader2 className="animate-spin" size={15} /> : <Rocket size={15} />} Publish this product
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
